/**
 * Signup Page
 *
 * @module pages/auth/Signup
 */

import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Mail, Lock, User, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { signUpWithEmail } from "@/config/supabase";
import { MIN_PASSWORD_LENGTH } from "@/config/auth";
import { START_PAGE } from "@/config/startPage";
import { logError } from '../../services/logger';
import { YDMWordmark, BrandFooter } from "@/components/brand";
import { Input } from "@/components";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  workspaceName?: string;
}

interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
}

function getPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= MIN_PASSWORD_LENGTH) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  score = Math.min(score, 4);

  const map: Record<number, PasswordStrength> = {
    // A translation key, not a label: this function is module-level and cannot
    // call t(). The render site translates it.
    0: { score: 0, label: "auth.passwordStrength.veryWeak", color: "bg-error-500" },
    1: { score: 1, label: "auth.passwordStrength.weak", color: "bg-error-500" },
    2: { score: 2, label: "auth.passwordStrength.fair", color: "bg-amber-500" },
    3: { score: 3, label: "auth.passwordStrength.good", color: "bg-lime-500" },
    4: { score: 4, label: "auth.passwordStrength.strong", color: "bg-success-500" },
  };
  return map[score];
}

const Signup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const validateField = useCallback((field: keyof FieldErrors, _value: string, allValues?: Record<string, string>): string | undefined => {
    const vals = allValues || { email, password, confirmPassword, workspaceName };
    switch (field) {
      case "email":
        if (!vals.email?.trim()) return t("auth.errors.emailRequired");
        if (!EMAIL_REGEX.test(vals.email)) return t("auth.errors.emailInvalid");
        return undefined;
      case "password":
        if (!vals.password) return t("auth.errors.passwordRequired");
        if (vals.password.length < MIN_PASSWORD_LENGTH) return t("auth.errors.passwordTooShort", { min: MIN_PASSWORD_LENGTH });
        return undefined;
      case "confirmPassword":
        if (!vals.confirmPassword) return t("auth.errors.confirmRequired");
        if (vals.confirmPassword !== vals.password) return t("auth.errors.passwordsDoNotMatch");
        return undefined;
      case "workspaceName":
        if (!vals.workspaceName?.trim()) return t("auth.errors.workspaceRequired");
        return undefined;
      default:
        return undefined;
    }
  }, [email, password, confirmPassword, workspaceName, t]);

  const handleBlur = useCallback((field: keyof FieldErrors) => {
    const allValues = { email, password, confirmPassword, workspaceName };
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, allValues[field], allValues) }));
  }, [email, password, confirmPassword, workspaceName, validateField]);

  const validateAll = useCallback((): boolean => {
    const errors: FieldErrors = {};
    if (!email.trim()) errors.email = t("auth.errors.emailRequired");
    else if (!EMAIL_REGEX.test(email)) errors.email = t("auth.errors.emailInvalid");
    if (!password) errors.password = t("auth.errors.passwordRequired");
    else if (password.length < MIN_PASSWORD_LENGTH) errors.password = t("auth.errors.passwordTooShort", { min: MIN_PASSWORD_LENGTH });
    if (!confirmPassword) errors.confirmPassword = t("auth.errors.confirmRequired");
    else if (confirmPassword !== password) errors.confirmPassword = t("auth.errors.passwordsDoNotMatch");
    if (!workspaceName.trim()) errors.workspaceName = t("auth.errors.workspaceRequired");
    setFieldErrors(errors);
    setTouched({ email: true, password: true, confirmPassword: true, workspaceName: true });
    return Object.keys(errors).length === 0;
  }, [email, password, confirmPassword, workspaceName, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!validateAll()) return;

    setIsLoading(true);
    try {
      const result = await signUpWithEmail(email, password, t, {
        first_name: firstName,
        last_name: lastName,
        workspace_name: workspaceName,
      });

      if (result.success) {
        if (result.requiresVerification) {
          setSuccess(true);
        } else {
          navigate(START_PAGE, { replace: true });
        }
      } else {
        setFormError(result.error || t("auth.createAccountFailed"));
      }
    } catch (err) {
      setFormError(t("auth.unexpectedError"));
      logError('Auth', 'Signup error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const hasErrors = Object.values(fieldErrors).some(Boolean);
  const strength = password ? getPasswordStrength(password) : null;

  if (success) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-700" />
            </div>
            <h2 className="text-xl font-semibold text-ink-900 mb-2">
              {t("auth.checkYourEmail")}
            </h2>
            {/* <Trans>, not dangerouslySetInnerHTML: the sentence wraps the
                address in <strong> and Thai places it differently from English,
                so the emphasis belongs in the translated string — but `email` is
                whatever the visitor typed, and this project sets
                interpolation.escapeValue: false. Trans renders through React, so
                the address stays text. */}
            <p className="text-neutral-700 mb-6">
              <Trans i18nKey="auth.verificationSent" values={{ email }} />
            </p>
            <Link
              to="/auth/signin"
              className="text-ink-900 underline hover:text-ink-950 font-medium"
            >
              {t("auth.backToSignin")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-500 flex flex-col items-center justify-center p-4 text-ink-950 relative overflow-hidden">
      {/* IDEAS gradient accents — vibrant bright per brand guideline p.22. Kept
          lighter blur radius so the browser can paint quickly. */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-ideas-orange/40 rounded-full blur-2xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-ideas-pink/30 rounded-full blur-2xl" />
      </div>

      <div className="relative max-w-md w-full">
        {/* Hero brand lockup — black logo on yellow */}
        <div className="text-center mb-8">
          <YDMWordmark variant="hero" bg="yellow" withTagline size="xl" />
          <h1 className="mt-6 font-display italic font-bold text-3xl sm:text-4xl text-ink-950 leading-tight">
            <Trans i18nKey="auth.makeItUnstoppable" />
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.3em] text-ink-900 font-display font-semibold">
            {t("auth.mediaIntelligence")}
          </p>
        </div>

        {/* Form — white card with bold black border to anchor it on the yellow */}
        <div className="bg-white text-ink-900 rounded-2xl shadow-ink border-2 border-ink-950 p-8">
          {/* Form-level error (from server/auth) */}
          {formError && (
            <div role="alert" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
              <span className="text-sm text-red-800">{formError}</span>
            </div>
          )}

          {/* Field-level error summary — shows all validation errors at once */}
          {hasErrors && (
            <div role="alert" aria-live="assertive" className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-semibold text-amber-900 mb-1">{t("auth.errors.fixFollowing")}</p>
              <ul className="text-sm text-amber-800 list-disc list-inside">
                {fieldErrors.email && <li>{fieldErrors.email}</li>}
                {fieldErrors.password && <li>{fieldErrors.password}</li>}
                {fieldErrors.confirmPassword && <li>{fieldErrors.confirmPassword}</li>}
                {fieldErrors.workspaceName && <li>{fieldErrors.workspaceName}</li>}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                id="firstName"
                label={t("auth.firstName")}
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t("auth.firstNamePlaceholder")}
                disabled={isLoading}
                leftIcon={<User className="w-5 h-5 text-neutral-700" />}
                className="py-3 disabled:bg-neutral-100"
              />
              <Input
                id="lastName"
                label={t("auth.lastName")}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t("auth.lastNamePlaceholder")}
                disabled={isLoading}
                className="py-3 disabled:bg-neutral-100"
              />
            </div>

            {/* Workspace name */}
            <div>
              <Input
                id="workspaceName"
                label={`${t("auth.workspaceName")} *`}
                type="text"
                value={workspaceName}
                onChange={(e) => {
                  setWorkspaceName(e.target.value);
                  if (touched.workspaceName) setFieldErrors((prev) => ({ ...prev, workspaceName: validateField("workspaceName", e.target.value) }));
                }}
                onBlur={() => handleBlur("workspaceName")}
                placeholder={t("auth.workspaceNamePlaceholder")}
                disabled={isLoading}
                error={touched.workspaceName ? fieldErrors.workspaceName : undefined}
                aria-invalid={touched.workspaceName && !!fieldErrors.workspaceName}
                className="py-3 disabled:bg-neutral-100"
              />
            </div>

            {/* Email */}
            <div>
              <Input
                id="email"
                label={`${t("auth.emailAddress")} *`}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (touched.email) setFieldErrors((prev) => ({ ...prev, email: validateField("email", e.target.value) }));
                }}
                onBlur={() => handleBlur("email")}
                placeholder="you@example.com"
                disabled={isLoading}
                leftIcon={<Mail className="w-5 h-5 text-neutral-700" />}
                error={touched.email ? fieldErrors.email : undefined}
                aria-invalid={touched.email && !!fieldErrors.email}
                className="py-3 disabled:bg-neutral-100"
              />
            </div>

            {/* Password with strength meter */}
            <div>
              <Input
                id="password"
                label={`${t("auth.password")} *`}
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (touched.password) setFieldErrors((prev) => ({ ...prev, password: validateField("password", e.target.value) }));
                }}
                onBlur={() => handleBlur("password")}
                placeholder={t("auth.passwordMinPlaceholder", { min: MIN_PASSWORD_LENGTH })}
                disabled={isLoading}
                leftIcon={<Lock className="w-5 h-5 text-neutral-700" />}
                error={touched.password ? fieldErrors.password : undefined}
                aria-invalid={touched.password && !!fieldErrors.password}
                aria-describedby="password-strength"
                className="py-3 disabled:bg-neutral-100"
              />
              {/* Password strength meter */}
              {password && (
                <div id="password-strength" className="mt-2" aria-live="polite">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 grid grid-cols-4 gap-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`h-1.5 rounded-full transition-colors ${
                            strength && i < strength.score ? strength.color : "bg-neutral-200"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-neutral-700 min-w-[70px]">
                      {strength ? t(strength.label) : null}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Input
                id="confirmPassword"
                label={`${t("auth.confirmPassword")} *`}
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (touched.confirmPassword) setFieldErrors((prev) => ({ ...prev, confirmPassword: validateField("confirmPassword", e.target.value) }));
                }}
                onBlur={() => handleBlur("confirmPassword")}
                placeholder={t("auth.confirmPasswordPlaceholder")}
                disabled={isLoading}
                leftIcon={<Lock className="w-5 h-5 text-neutral-700" />}
                error={touched.confirmPassword ? fieldErrors.confirmPassword : undefined}
                aria-invalid={touched.confirmPassword && !!fieldErrors.confirmPassword}
                className="py-3 disabled:bg-neutral-100"
              />
            </div>

            {/* Submit — BLACK button (primary CTA on yellow canvas inverts to black) */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-ink-950 text-primary-500 font-bold uppercase tracking-wide rounded-lg hover:bg-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-ink"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t("auth.creatingAccount")}
                </>
              ) : (
                t("auth.createAccount")
              )}
            </button>
          </form>

          {/* Sign in link */}
          <p className="mt-6 text-center text-sm text-ink-700">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link
              to="/auth/signin"
              className="font-bold text-ink-950 hover:text-ink-700 underline underline-offset-2 decoration-2"
            >
              {t("auth.signin")}
            </Link>
          </p>
        </div>

        {/* Brand attribution — on yellow canvas */}
        <BrandFooter bg="yellow" className="mt-8" />
      </div>
    </div>
  );
};

export default Signup;
